import type { DesignDocument, GuestContactMethod } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useId, useState } from 'react';
import {
  earliestDesiredDate,
  GUEST_DESTINATION_HINT,
  QUOTE_REQUEST_TEXT_LIMITS,
  SizeRemainingSummary,
  SizeTotalFields,
} from '@/entities/quote';
import {
  GuestContactFields,
  GuestDesignSummary,
  GuestDestinationCard,
  type GuestDestinationValues,
  GuestPrivacyConsent,
  type GuestQuoteRequestFormData,
  type GuestQuoteRequestReceipt,
  useSubmitGuestQuoteRequest,
} from '@/features/guest-quote-request';
import {
  DestinationAddButton,
  EstimateSummary,
  PrintMethodField,
  TotalQuantity,
  useQuoteRequestDestinations,
} from '@/features/quote-request';
import { Button, cn, FormField, FormSection, Label } from '@/shared/ui';

export interface GuestQuoteRequestFormProps {
  readonly form: GuestQuoteRequestFormData;
  /** 에디터 임시 작업 — 페이지가 상품·색상이 맞는지 확인해 넘긴다. */
  readonly design: DesignDocument;
  /** 처음 수량(에디터에서 고른 수량) — 없거나 최소 주문 수량 미만이면 최소 주문 수량으로 시작한다. */
  readonly initialQuantity?: number | null;
  /** 에디터가 남긴 앞면 미리보기 그림(이미지 data URL) — 페이지가 읽어 넘기고, 없으면 글 요약만 보여 준다. */
  readonly designPreviewSrc?: string | null;
  /** 접수되면 결과로 부른다 — 페이지가 임시 작업을 지우고 완료 화면을 보여 준다. */
  readonly onSubmitted: (receipt: GuestQuoteRequestReceipt) => void;
}

const {
  memo: MEMO_MAX_LENGTH,
  companyName: COMPANY_NAME_MAX_LENGTH,
  contactName: CONTACT_NAME_MAX_LENGTH,
} = QUOTE_REQUEST_TEXT_LIMITS;
/** 칸 높이 44px — 휴대폰에서도 누르기 쉬운 크기. */
const INPUT_CLASS = 'h-11';
const CONSENT_HINT = '개인정보 수집·이용에 동의하면 상담을 요청할 수 있어요.';

/** 인쇄 방식이 하나뿐이면 미리 고른다(견적 설계 §5-4). */
const initialPrintMethodId = (methods: GuestQuoteRequestFormData['printMethods']): string =>
  methods.length === 1 ? (methods[0]?.id ?? '') : '';

/** 추가한 카드는 빈 칸으로 시작한다 — 첫 카드만 최소 주문 수량(spec §6-1). */
const blankDestination = (): GuestDestinationValues => ({
  postalCode: '',
  address1: '',
  quantity: '',
  sizes: [],
});

/**
 * 비회원 견적 요청 폼(비회원 견적 설계 G12) — 회원 견적 요청 폼과 같은 결(상품 상세와 같은 두 칸, 번호 붙은 묶음, 따라 내려오는 임시 견적).
 * 왼쪽: 01 인쇄·납기 → 02 배송지·수량 → 03 요청 정보(회사·담당자·메모) → 04 연락 방법(이메일 또는 휴대폰 하나) → 05 개인정보 수집·이용 동의.
 * 오른쪽: 디자인 정보(앞면 미리보기·상품·색상·에디터로 돌아가기)와 임시 견적·[상담 요청]. 배송지는 우편번호·기본주소·수량만 받는다
 * (SUPER-86 D8 — 받는 분·연락처·상세주소는 주문할 때).
 * 동의 전에는 [상담 요청]을 잠그고 그 이유를 버튼 설명(aria-describedby)으로 단다(주문하기 동의와 같은 방식).
 */
export const GuestQuoteRequestForm = ({
  form,
  design,
  initialQuantity,
  designPreviewSrc,
  onSubmitted,
}: GuestQuoteRequestFormProps): ReactElement => {
  // 처음 값만 받는다 — 요청 정보가 다시 들어와도 고객이 적은 값은 그대로다.
  const destinations = useQuoteRequestDestinations<GuestDestinationValues>({
    sizeOptions: form.sizeOptions,
    minOrderQty: form.minOrderQty,
    initialQuantity,
    blankDestination,
  });
  const [printMethodId, setPrintMethodId] = useState(initialPrintMethodId(form.printMethods));
  const [desiredDate, setDesiredDate] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [memo, setMemo] = useState('');
  const [contactMethod, setContactMethod] = useState<GuestContactMethod>('EMAIL');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const memoId = useId();
  const memoErrorId = `${memoId}-error`;
  const consentHintId = useId();
  const { fieldErrors, destinationErrors, formError, isPending, submit } =
    useSubmitGuestQuoteRequest({
      minOrderQty: form.minOrderQty,
      priceTiers: form.priceTiers,
      shippingFeeSettings: form.shippingFeeSettings,
      sizeOptions: form.sizeOptions,
    });
  const { hasSizes, sizeFields, totalQuantity, showSizeRows } = destinations;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const receipt = await submit({
      productId: form.productId,
      colorOptionId: form.colorOptionId,
      design,
      sizeFields,
      destinations: destinations.toSubmissionDestinations(),
      printMethodId,
      desiredDate,
      companyName,
      contactName,
      memo,
      contactMethod,
      email,
      phone,
      privacyConsent,
    });

    if (receipt !== null) {
      onSubmitted(receipt);
    }
  };

  return (
    <form
      aria-label="비회원 견적 요청"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:grid-rows-[auto_1fr] lg:gap-x-12 lg:gap-y-10 xl:gap-x-16"
      noValidate
    >
      <GuestDesignSummary
        productId={form.productId}
        productName={form.productName}
        colorName={form.colorName}
        previewSrc={designPreviewSrc}
        className="lg:col-start-2 lg:row-start-1"
      />

      <div className="flex min-w-0 flex-col gap-12 lg:col-start-1 lg:row-span-2 lg:row-start-1">
        <FormSection order={1} title="인쇄·납기">
          <div className="grid gap-5 sm:grid-cols-2">
            <PrintMethodField
              value={printMethodId}
              methods={form.printMethods}
              error={fieldErrors.printMethodId}
              onChange={setPrintMethodId}
            />

            <FormField
              label="희망 납기일"
              type="date"
              description="선택 사항 — 내일 이후 날짜만 받습니다."
              min={earliestDesiredDate(new Date())}
              className={INPUT_CLASS}
              value={desiredDate}
              error={fieldErrors.desiredDate}
              onChange={(event) => setDesiredDate(event.target.value)}
            />
          </div>
        </FormSection>

        <FormSection order={2} title="배송지·수량">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-1 text-sm text-muted-foreground">
              <p>
                {hasSizes
                  ? '사이즈별 수량을 적고, 배송지가 둘 이상이면 배송지마다 사이즈를 나눠 주세요.'
                  : '배송지마다 수량을 나눠 주세요. 배송비는 배송지마다 붙어요.'}
              </p>
              <p>{GUEST_DESTINATION_HINT}</p>
            </div>

            {hasSizes ? (
              <SizeTotalFields
                options={form.sizeOptions}
                values={sizeFields}
                error={fieldErrors.quantity}
                onChange={destinations.changeSizeField}
              />
            ) : null}

            <TotalQuantity
              total={totalQuantity}
              minOrderQty={form.minOrderQty}
              error={hasSizes ? undefined : fieldErrors.quantity}
            />

            {destinations.items.map((item, index) => (
              <GuestDestinationCard
                key={item.key}
                order={index + 1}
                value={item.value}
                errors={destinationErrors[item.key] ?? {}}
                canRemove={destinations.canRemove}
                sizeOptions={form.sizeOptions}
                showQuantity={!hasSizes}
                showSizeRows={showSizeRows}
                onChange={(patch) => destinations.update(item.key, patch)}
                onRemove={() => destinations.remove(item.key)}
              />
            ))}

            {showSizeRows ? <SizeRemainingSummary remaining={destinations.remainingSizes} /> : null}

            {fieldErrors.destinations ? (
              <p role="alert" className="text-sm text-destructive">
                {fieldErrors.destinations}
              </p>
            ) : null}

            <DestinationAddButton count={destinations.items.length} onAdd={destinations.add} />
          </div>
        </FormSection>

        <FormSection order={3} title="요청 정보">
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              label="회사·단체명"
              autoComplete="organization"
              description={`선택 사항, ${COMPANY_NAME_MAX_LENGTH}자 이하`}
              maxLength={COMPANY_NAME_MAX_LENGTH}
              className={INPUT_CLASS}
              value={companyName}
              error={fieldErrors.companyName}
              onChange={(event) => setCompanyName(event.target.value)}
            />

            <FormField
              label="담당자명"
              autoComplete="name"
              isRequired
              required
              maxLength={CONTACT_NAME_MAX_LENGTH}
              className={INPUT_CLASS}
              value={contactName}
              error={fieldErrors.contactName}
              onChange={(event) => setContactName(event.target.value)}
            />

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor={memoId}>요청 메모</Label>
              <textarea
                id={memoId}
                rows={5}
                maxLength={MEMO_MAX_LENGTH}
                value={memo}
                aria-invalid={fieldErrors.memo ? true : undefined}
                aria-describedby={fieldErrors.memo ? memoErrorId : undefined}
                className={cn(
                  'min-h-32 w-full resize-y rounded-2xl border border-input bg-background px-4 py-3 text-sm leading-relaxed transition-colors',
                  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  fieldErrors.memo && 'border-destructive focus-visible:ring-destructive',
                )}
                placeholder={`선택 사항, ${MEMO_MAX_LENGTH.toLocaleString('ko-KR')}자 이하`}
                onChange={(event) => setMemo(event.target.value)}
              />
              {fieldErrors.memo ? (
                <p id={memoErrorId} role="alert" className="text-xs text-destructive">
                  {fieldErrors.memo}
                </p>
              ) : null}
            </div>
          </div>
        </FormSection>

        <FormSection order={4} title="연락 방법">
          <GuestContactFields
            method={contactMethod}
            email={email}
            phone={phone}
            emailError={fieldErrors.email}
            phoneError={fieldErrors.phone}
            onMethodChange={setContactMethod}
            onEmailChange={setEmail}
            onPhoneChange={setPhone}
          />
        </FormSection>

        <FormSection order={5} title="개인정보 수집·이용 동의">
          <GuestPrivacyConsent
            checked={privacyConsent}
            error={fieldErrors.privacyConsent}
            onChange={setPrivacyConsent}
          />
        </FormSection>
      </div>

      <div className="flex flex-col gap-5 lg:sticky lg:top-24 lg:col-start-2 lg:row-start-2 lg:self-start">
        <EstimateSummary
          tiers={form.priceTiers}
          minOrderQty={form.minOrderQty}
          totalQuantity={totalQuantity}
          postalCodes={destinations.items.map((item) => item.value.postalCode)}
          shippingFeeSettings={form.shippingFeeSettings}
        />

        {formError ? (
          <p
            role="alert"
            className="border-l-2 border-destructive bg-destructive/5 px-5 py-4 text-sm text-destructive"
          >
            {formError}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            size="lg"
            isLoading={isPending}
            disabled={!privacyConsent}
            aria-describedby={privacyConsent ? undefined : consentHintId}
            className="h-14 w-full text-base"
          >
            상담 요청
          </Button>
          {privacyConsent ? null : (
            <p id={consentHintId} className="text-center text-xs text-muted-foreground">
              {CONSENT_HINT}
            </p>
          )}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          임시 견적이며 확정 견적은 운영자가 검토 후 발행합니다.
        </p>
      </div>
    </form>
  );
};
